import type {
  QuestionBrief,
  QuestionBriefNumber,
  QuestionBriefProductUsage,
  QuestionBriefWasteStreamAllocation,
  QuestionBriefWasteStreamAttestedZero,
  QuestionBriefWasteStreamDirectInput,
  QuestionnaireStatusItem,
  ReleaseProfileProcessWithChildren,
  ThresholdBrief,
} from '../types/tri.ts';
import type {
  CasOrCategory,
  ReleaseProfileQuestionID,
  ProductID,
  FacilityID,
  ReleaseProfileAttestationState,
  ReleaseProfileID,
  UsageUnits,
} from '../types/db.ts';
import type {
  MixComponent,
  ReleaseProfileWasteStream,
  ReleaseProfileFormulaChoiceID,
  WasteStreamID,
  ReleaseProfileFormulaChoiceListID,
  ReleaseProfilesAndVariables,
  FormulaOutputType,
} from '../types/release-profiles.ts';
import { wasteTransferExistsAndHasAllValues, wasteStreamAllocationWarning } from './waste-streams.ts';
import { facilityWasteStreamTotalsCalculate, zeroReleasesWasteStreamID } from './releases-calculate.ts';
import { stateSpecificGuidanceByState } from './state-specific-guidance.ts';
import { VariableNameFinder } from './formulas/variable-name-finder.ts';
import {
  productSolidsPercentOfVolumeVar,
  productSolidsPercentOfWeightVar,
  productUsageGalVar,
  productUsageLbVar,
  rainfallVar,
} from './formulas/formula-utils.ts';
import { UnitsTracker } from './formulas/units.ts';
import type {
  ChemicalActivitiesAndUses,
  QuestionnaireAnswers,
  QuestionnaireFormulaChoicesContainer,
  WasteTransfer,
} from '../types/questionnaires.ts';
import type { FormRAssignmentLine, FormRLeafLine } from './form-r/form-r-sections.ts';
import { formRChemicalActivitiesAndUsesIsComplete } from './form-r/form-r-chemical-activities-and-uses.ts';
import { generateId } from '../../cross-app-shared/services/generate-id.ts';
import { formulaElaboratedToValidOutputs, releaseProfileAnnotatableVariableNames } from './release-profiles.ts';
import { dioxinCongenerToIndex } from '../data/tri-dioxins.ts';

export function questionnaireCreateBlank(): QuestionnaireAnswers {
  return {
    releaseProfileAnswers: {},
    releaseProfileAnswersByProduct: {},
    releaseProfileFormulaChoices: {},
    releaseProfileChemicalNaAnswers: {},
    releaseProfileSourceDocumentation: {},
    releaseProfileAnswerDeviations: {},
    releaseProfileAnswerDisplayUnits: {},
    wasteTransfers: {},
    anySignificantChanges: '',
    stateSpecificPlanning: '',
    chemicalActivitiesAndUses: {},
    maxOnSiteAmount: {
      method: 'inventory-backlog-facility-wide',
      inventoryBacklogInWeeksOnSite: '',
      perProductBacklogWeeks: [],
      perProductMaxQuantities: [],
      chemicalPeakPounds: {},
      chemicalOverrideRangeCodes: {},
    },
    streams: {},
    potws: {},
    offSiteLocations: {},
    wasteTreatment: {
      isApplicable: null,
      treatments: {},
    },
    onSiteEnergyRecovery: {},
    onSiteRecycling: {},
    productionOrActivityRatio: {
      type: null,
      priorYearValue: '',
      currentYearValue: '',
      isChemicalSpecific: false,
      byChemical: {},
    },
    sourceReductionActivities: {
      isApplicable: null,
      activities: [],
    },
    formRVsFormA: {},
    potwDistributionsKnown: {},
    answered: {},
  };
}

export function questionnaireChemicalActivitiesAndUsesCreateBlank(): ChemicalActivitiesAndUses {
  return {
    manufacture: {
      produce: false,
      import: false,
      onSiteUseOrProcessing: false,
      saleOrDistribution: false,
      byProduct: false,
      impurity: false,
    },
    process: {
      reactantCategories: [],
      formulationComponents: [],
      articleComponent: false,
      repackaging: false,
      impurity: false,
      recycling: false,
    },
    otherwiseUse: {
      chemicalProcessingAid: [],
      manufacturingAid: [],
      ancillaryOrOtherUse: [],
    },
  };
}

export function wasteTransferAddToAnswers(
  answers: QuestionnaireAnswers,
  question:
    | QuestionBriefWasteStreamDirectInput
    | QuestionBriefWasteStreamAllocation
    | QuestionBriefWasteStreamAttestedZero,
  allocationOrAnswer: WasteTransfer['allocationOrAnswer'],
  type: 'stream' | 'potw' | 'offSiteLocation' | 'nothing',
): WasteTransfer {
  const common = {
    wasteStreamID: question.wasteStreamID,
    allocationOrAnswer,
  };

  if (allocationOrAnswer === '' && question.type === 'wasteStreamAttestedZero') {
    common.allocationOrAnswer = 0;
  }

  const transferID = generateId('transfer');
  if (type === 'stream') {
    answers.wasteTransfers[transferID] = {
      ...common,
      transferID,
      type: 'stream',
      streamID: '',
    };
  }
  if (type === 'potw') {
    answers.wasteTransfers[transferID] = {
      ...common,
      transferID,
      type: 'potw',
      potwID: '',
    };
  }
  if (type === 'offSiteLocation') {
    answers.wasteTransfers[transferID] = {
      ...common,
      transferID,
      type: 'offSiteLocation',
      offSiteLocationID: '',
      wasteType: '',
    };
  }
  if (type === 'nothing') {
    answers.wasteTransfers[transferID] = {
      ...common,
      transferID,
      type: 'nothing',
    };
  }

  return answers.wasteTransfers[transferID]!;
}

export function mixComponentToQuestionID(component: MixComponent): ReleaseProfileQuestionID {
  return `question_${component.mixRowID}`;
}

export function wasteStreamToQuestionID(component: ReleaseProfileWasteStream): ReleaseProfileQuestionID {
  return `question_${component.definition.wasteStreamID}`;
}

function recordNumberResult(
  result: Map<ReleaseProfileQuestionID, QuestionBrief>,
  args: {
    baseQuestionID: ReleaseProfileQuestionID;
    basePrompt: string;
    units: string;
    formRLine: FormRAssignmentLine | '' | null;
    processName: string;
    releaseProfileName: string;
    casOrCategory: CasOrCategory | null;
    formulaOutputType: FormulaOutputType;
    isAutoFilledFromPriorYear: boolean;
    facilityProvidesSourceDocumentation: boolean;
  },
) {
  const questionID = numberQuestionID(args.baseQuestionID, args.casOrCategory);

  if (!result.has(questionID)) {
    let range = '';
    if (args.units !== 'Scalar') {
      const convertedForRange = UnitsTracker.fromParsedUnitString(args.units).convertToScalarIfPercentage(1);
      if (convertedForRange !== false) {
        range = `0-${(1 / convertedForRange).toLocaleString()}`;
      }
    }

    result.set(questionID, {
      questionID,
      baseQuestionID: args.baseQuestionID,
      basePrompt: args.basePrompt,
      prompt: args.casOrCategory ? `[CAS:${args.casOrCategory}]: ${args.basePrompt}` : args.basePrompt,
      units: args.units,
      range,
      formRLines: [],
      processNames: [],
      releaseProfileNames: [],
      type: 'number',
      isChemicalSpecific: args.casOrCategory !== null,
      casOrCategory: args.casOrCategory,
      formulaOutputTypes: [args.formulaOutputType],
      isAutoFilledFromPriorYear: args.isAutoFilledFromPriorYear,
      facilityProvidesSourceDocumentation: args.facilityProvidesSourceDocumentation,
    });
  }

  const brief = result.get(questionID)!;
  if (args.formRLine && !brief.formRLines.includes(args.formRLine)) {
    brief.formRLines.push(args.formRLine);
  }

  if (!brief.releaseProfileNames.includes(args.releaseProfileName)) {
    brief.releaseProfileNames.push(args.releaseProfileName);
  }

  if (!brief.processNames.includes(args.processName)) {
    brief.processNames.push(args.processName);
  }

  if (!brief.formulaOutputTypes.includes(args.formulaOutputType)) {
    brief.formulaOutputTypes.push(args.formulaOutputType);
  }

  // Preserve facilityProvidesSourceDocumentation when merging (e.g. same variable in multiple profiles)
  if (args.facilityProvidesSourceDocumentation === true) {
    (brief as QuestionBriefNumber).facilityProvidesSourceDocumentation = true;
  }
}

function recordProductSolidsResult(
  result: Map<ReleaseProfileQuestionID, QuestionBrief>,
  args: {
    questionID: ReleaseProfileQuestionID;
    productID: ProductID;
    subType: 'solidsPercentageByWeight' | 'solidsPercentageByVolume';
    prompt: string;
    formRLine: FormRAssignmentLine | '' | null;
    processName: string;
    releaseProfileName: string;
    formulaOutputType: FormulaOutputType;
  },
) {
  if (!result.has(args.questionID)) {
    result.set(args.questionID, {
      questionID: args.questionID,
      productID: args.productID,
      prompt: args.prompt,
      units: '%',
      range: '0-100',
      formRLines: [],
      processNames: [],
      releaseProfileNames: [],
      formulaOutputTypes: [args.formulaOutputType],
      type: 'productSolids',
      subType: args.subType,
    });
  }

  const brief = result.get(args.questionID)!;
  if (args.formRLine && !brief.formRLines.includes(args.formRLine)) {
    brief.formRLines.push(args.formRLine);
  }

  if (!brief.releaseProfileNames.includes(args.releaseProfileName)) {
    brief.releaseProfileNames.push(args.releaseProfileName);
  }

  if (!brief.processNames.includes(args.processName)) {
    brief.processNames.push(args.processName);
  }

  if (!brief.formulaOutputTypes.includes(args.formulaOutputType)) {
    brief.formulaOutputTypes.push(args.formulaOutputType);
  }
}

function recordProductUsageResult(
  result: Map<ReleaseProfileQuestionID, QuestionBrief>,
  args: {
    questionID: ReleaseProfileQuestionID;
    productID: ProductID;
    specificGravity: number | null;
    units: UsageUnits;
    prompt: string;
    formRLine: FormRAssignmentLine | '' | null;
    processName: string;
    releaseProfileName: string;
    formulaOutputType: FormulaOutputType;
  },
) {
  if (!result.has(args.questionID)) {
    result.set(args.questionID, {
      questionID: args.questionID,
      productID: args.productID,
      prompt: args.prompt,
      specificGravity: args.specificGravity,
      units: args.units,
      formRLines: [],
      processNames: [],
      releaseProfileNames: [],
      formulaOutputTypes: [args.formulaOutputType],
      type: 'productUsage',
    });
  }

  const brief = result.get(args.questionID)!;
  if (args.formRLine && !brief.formRLines.includes(args.formRLine)) {
    brief.formRLines.push(args.formRLine);
  }

  if (!brief.releaseProfileNames.includes(args.releaseProfileName)) {
    brief.releaseProfileNames.push(args.releaseProfileName);
  }

  if (!brief.processNames.includes(args.processName)) {
    brief.processNames.push(args.processName);
  }

  if (!brief.formulaOutputTypes.includes(args.formulaOutputType)) {
    brief.formulaOutputTypes.push(args.formulaOutputType);
  }
}

function recordReleaseProfileResult(
  result: Map<ReleaseProfileQuestionID, QuestionBrief>,
  args: {
    questionID: ReleaseProfileQuestionID;
    prompt: string;
    formRLine: FormRAssignmentLine | '' | null;
    processName: string;
    releaseProfileName: string;
    choiceListID: ReleaseProfileFormulaChoiceListID;
    choices: { choiceID: ReleaseProfileFormulaChoiceID; title: string }[];
    formulaOutputType: FormulaOutputType;
  },
) {
  if (!result.has(args.questionID)) {
    result.set(args.questionID, {
      questionID: args.questionID,
      prompt: args.prompt,
      formRLines: [],
      processNames: [],
      releaseProfileNames: [],
      formulaOutputTypes: [args.formulaOutputType],
      choiceListID: args.choiceListID,
      choices: args.choices,
      type: 'releaseProfileFormulaChoice',
    });
  }

  const brief = result.get(args.questionID)!;
  if (args.formRLine && !brief.formRLines.includes(args.formRLine)) {
    brief.formRLines.push(args.formRLine);
  }

  if (!brief.releaseProfileNames.includes(args.releaseProfileName)) {
    brief.releaseProfileNames.push(args.releaseProfileName);
  }

  if (!brief.processNames.includes(args.processName)) {
    brief.processNames.push(args.processName);
  }

  if (!brief.formulaOutputTypes.includes(args.formulaOutputType)) {
    brief.formulaOutputTypes.push(args.formulaOutputType);
  }
}

function recordWasteStreamResult(
  result: Map<ReleaseProfileQuestionID, QuestionBrief>,
  args: {
    questionID: ReleaseProfileQuestionID;
    type: 'wasteStreamDirectInput' | 'wasteStreamAllocation';
    wasteStreamID: WasteStreamID;
    prompt: string;
    units: string;
    formRLine: FormRAssignmentLine | '' | null;
    processName: string;
    releaseProfileName: string;
    formulaOutputType: FormulaOutputType;
  },
) {
  if (!result.has(args.questionID)) {
    result.set(args.questionID, {
      questionID: args.questionID,
      wasteStreamID: args.wasteStreamID,
      prompt: args.prompt,
      units: args.units,
      formRLines: [],
      processNames: [],
      releaseProfileNames: [],
      formulaOutputTypes: [args.formulaOutputType],
      type: args.type,
    });
  }

  const brief = result.get(args.questionID)!;
  if (args.formRLine && !brief.formRLines.includes(args.formRLine)) {
    brief.formRLines.push(args.formRLine);
  }

  if (!brief.releaseProfileNames.includes(args.releaseProfileName)) {
    brief.releaseProfileNames.push(args.releaseProfileName);
  }

  if (!brief.processNames.includes(args.processName)) {
    brief.processNames.push(args.processName);
  }

  if (!brief.formulaOutputTypes.includes(args.formulaOutputType)) {
    brief.formulaOutputTypes.push(args.formulaOutputType);
  }
}

export interface ChemicalSpecificQuestionsForcedBrief {
  casOrCategory: CasOrCategory;
  questionID: ReleaseProfileQuestionID;
}

export function zeroReleasesQuestionID(
  formRLine: FormRLeafLine,
  casOrCategory: CasOrCategory,
): ReleaseProfileQuestionID {
  return `question_zero_${formRLine}_${casOrCategory}`;
}

export function formulaChoiceQuestionID(choiceListID: ReleaseProfileFormulaChoiceListID): ReleaseProfileQuestionID {
  return `question_releaseProfileFormulaChoice_${choiceListID}`;
}

export function numberQuestionID(
  baseQuestionID: ReleaseProfileQuestionID,
  casOrCategory: CasOrCategory | null,
): ReleaseProfileQuestionID {
  return casOrCategory ? `${baseQuestionID}_${casOrCategory}` : baseQuestionID;
}

export function productSolidsQuestionID(
  productID: ProductID,
  weightOrVolume: 'weight' | 'volume',
): ReleaseProfileQuestionID {
  return `question_product_solids_percent_of_${weightOrVolume}_${productID}`;
}

export function productUsageQuestionID(productID: ProductID): ReleaseProfileQuestionID {
  return `question_product_usage_${productID}`;
}

export function wasteStreamAttestedZeroQuestion(
  formRLine: '5.3' | '6.1' | '6.2',
  casOrCategory: CasOrCategory,
): QuestionBriefWasteStreamAttestedZero {
  const questionID: ReleaseProfileQuestionID = zeroReleasesQuestionID(formRLine, casOrCategory);
  let receptor = 'receptor';
  if (formRLine === '5.3') {
    receptor = 'stream or body of water';
  } else if (formRLine === '6.1') {
    receptor = 'POTW';
  } else if (formRLine === '6.2') {
    receptor = 'off-site location';
  }

  return {
    questionID,
    wasteStreamID: zeroReleasesWasteStreamID(formRLine, casOrCategory),
    prompt: `It was indicated that 0 pounds of [CAS:${casOrCategory}] was transferred to a Form R line ${formRLine} ${receptor}. This means a waste stream containing the chemical was directed to the environmental medium, or leaks, spills or fugitive emissions can occur. Please indicate the ${receptor} where these chemicals could have gone.`,
    units: 'pounds',
    formRLines: [formRLine],
    processNames: [],
    releaseProfileNames: [],
    type: 'wasteStreamAttestedZero',
    formulaOutputTypes: ['actual'],
  };
}

// ReleaseProfileProcessWithChildren minimized for just what the questionnaire needs:
export interface QuestionnaireProcessAndProfiles {
  formRLine: FormRAssignmentLine | '' | null;
  processName: string;
  profiles: {
    releaseProfileID: ReleaseProfileID;
    products: {
      productID: ProductID;
      productName: string;
      specificGravity: number | null;
      usageUnits: UsageUnits;
    }[];
  }[];
}

export function thresholdChemicalsPlusOriginals(
  thresholdChemicals: Pick<ThresholdBrief, 'casOrCategory' | 'casOriginals'>[],
): CasOrCategory[] {
  const result = new Set<CasOrCategory>();
  for (const { casOrCategory, casOriginals } of thresholdChemicals) {
    result.add(casOrCategory);
    for (const casOriginal of casOriginals) {
      result.add(casOriginal);
    }
  }

  return Array.from(result);
}

export type QuestionnaireMode =
  | {
      type: 'triRelease';
      attestations: Record<FormRLeafLine, ReleaseProfileAttestationState>;
      chemicalSpecificQuestionsForced: ChemicalSpecificQuestionsForcedBrief[];
    }
  | {
      type: 'triThreshold';
      chemicalSpecificQuestionsForced: ChemicalSpecificQuestionsForcedBrief[];
      triChemicals: Set<CasOrCategory>;
    }
  | {
      type: 'emissions';
      acceptableOutputType: Set<FormulaOutputType>;
    };

export function questionsRequiredFromReleaseProfiles(
  mode: QuestionnaireMode,
  relevantChemicals: CasOrCategory[],
  releaseProfileFormulaChoices: QuestionnaireFormulaChoicesContainer,
  processes: QuestionnaireProcessAndProfiles[],
  {
    releaseProfiles,
    questions,
    mixCalculations,
    wasteStreams,
  }: Pick<ReleaseProfilesAndVariables, 'releaseProfiles' | 'questions' | 'mixCalculations' | 'wasteStreams'>,
  annotatableVariableNames: Set<string>,
): QuestionBrief[] {
  const emitProductUsageQuestions = mode.type === 'emissions';
  const result = new Map<ReleaseProfileQuestionID, QuestionBrief>();
  const questionsByVariableName = new Map(questions.map((q) => [q.nameForVariable, q]));
  const mixCalculationsByVariableName = new Map(mixCalculations.map((m) => [m.nameForVariable, m]));
  const wasteStreamsByVariableName = new Map(wasteStreams.map((m) => [m.nameForVariable, m]));
  const releaseProfilesById = new Map(releaseProfiles.map((p) => [p.releaseProfileID, p]));
  const chemicalSpecificQuestionsForcedByQuestionID = new Map<ReleaseProfileQuestionID, CasOrCategory[]>();
  if (mode.type === 'triRelease' || mode.type === 'triThreshold') {
    for (const { questionID, casOrCategory } of mode.chemicalSpecificQuestionsForced) {
      if (!chemicalSpecificQuestionsForcedByQuestionID.has(questionID)) {
        chemicalSpecificQuestionsForcedByQuestionID.set(questionID, []);
      }
      chemicalSpecificQuestionsForcedByQuestionID.get(questionID)!.push(casOrCategory);
    }
  }

  if (mode.type === 'triRelease') {
    const linesNeedingWasteTransfers = ['5.3', '6.1', '6.2'] as const satisfies FormRAssignmentLine[];
    for (const formRLine of linesNeedingWasteTransfers) {
      for (const casOrCategory of relevantChemicals) {
        if (mode.attestations[formRLine][casOrCategory]?.attestation === 'zero-releases') {
          const question = wasteStreamAttestedZeroQuestion(formRLine, casOrCategory);
          result.set(question.questionID, question);
        }
      }
    }
  }

  for (const process of processes) {
    for (const { releaseProfileID, products } of process.profiles) {
      const profile = releaseProfilesById.get(releaseProfileID);
      if (!profile) {
        console.warn('questionsRequiredFromReleaseProfiles: Unable to find profile:', releaseProfileID);
        continue;
      }

      let formulas: {
        formula: string;
        chemicalRestrictions: 'none' | 'noChemicals' | 'allVolatile' | 'allSolid' | Set<CasOrCategory>;
        formulaOutputType: FormulaOutputType;
      }[] = [];

      // Some formulas will get filtered out based on outputs, and if the filtered-out formulas are the only source
      // of formula-choice questions, we don't want to ask those questions:
      const allFormulasRegardlessOfChoices = formulaElaboratedToValidOutputs(
        mode,
        profile.formula,
        new Set(relevantChemicals),
        {}, // releaseProfileFormulaChoices
        annotatableVariableNames,
      );
      const choiceListsRelevant = new Map<ReleaseProfileFormulaChoiceListID, Set<FormulaOutputType>>();
      for (const { ifChoices, type: formulaOutputType } of allFormulasRegardlessOfChoices) {
        for (const { choiceListID } of ifChoices) {
          if (choiceListsRelevant.has(choiceListID)) {
            choiceListsRelevant.get(choiceListID)!.add(formulaOutputType);
          } else {
            choiceListsRelevant.set(choiceListID, new Set([formulaOutputType]));
          }
        }
      }

      for (const choiceList of Object.values(profile.formula.choiceLists)) {
        for (const formulaOutputType of choiceListsRelevant.get(choiceList.choiceListID) ?? []) {
          recordReleaseProfileResult(result, {
            questionID: formulaChoiceQuestionID(choiceList.choiceListID),
            prompt: choiceList.question,
            formRLine: process.formRLine,
            releaseProfileName: profile.name,
            processName: process.processName,
            choiceListID: choiceList.choiceListID,
            choices: choiceList.choices,
            formulaOutputType,
          });
        }
      }

      formulas = Array.from(
        formulaElaboratedToValidOutputs(
          mode,
          profile.formula,
          new Set(relevantChemicals),
          releaseProfileFormulaChoices,
          annotatableVariableNames,
        ),
      ).flatMap((item) => {
        // In TRI, we do not want questions to appear until the appropriate choices have been made, but in Emissions,
        // we use an empty map of choices to get the full list of questions for the UI:
        if (item.answerIsMissingForChoice && (mode.type === 'triRelease' || mode.type === 'triThreshold')) {
          return [];
        }

        return [
          {
            formula: item.formula,
            chemicalRestrictions:
              item.chemicals === 'inferred'
                ? 'none'
                : item.chemicals === 'noChemicals' || item.chemicals === 'allVolatile' || item.chemicals === 'allSolid'
                  ? item.chemicals
                  : new Set(item.chemicals),
            formulaOutputType: item.type,
          },
        ];
      });

      for (const { formula, chemicalRestrictions, formulaOutputType } of formulas) {
        const maybeVariableNames = VariableNameFinder.findIdentifiersInString(formula);
        if (maybeVariableNames.status === 'error') {
          console.error(`questionsRequiredFromReleaseProfiles:`, profile, maybeVariableNames.error);
          continue;
        }

        const identifiersToProcess = Array.from(maybeVariableNames.identifiers).reverse();

        while (identifiersToProcess.length > 0) {
          const { nameBare, annotation } = identifiersToProcess.pop()!;

          if (nameBare === rainfallVar) {
            recordNumberResult(result, {
              baseQuestionID: `question_rainfall`,
              basePrompt: 'How many total inches of rain fell on your facility during the year?',
              units: 'inches',
              formRLine: process.formRLine,
              releaseProfileName: profile.name,
              processName: process.processName,
              casOrCategory: null,
              formulaOutputType,
              isAutoFilledFromPriorYear: false,
              facilityProvidesSourceDocumentation: false,
            });
            continue;
          } else if (nameBare === productSolidsPercentOfWeightVar && products.length > 0) {
            for (const product of products) {
              recordProductSolidsResult(result, {
                questionID: productSolidsQuestionID(product.productID, 'weight'),
                productID: product.productID,
                formRLine: process.formRLine,
                releaseProfileName: profile.name,
                processName: process.processName,
                formulaOutputType,
                prompt: `By weight, what percentage of ${product.productName} is solids?`,
                subType: 'solidsPercentageByWeight',
              });
            }
            continue;
          } else if (nameBare === productSolidsPercentOfVolumeVar && products.length > 0) {
            for (const product of products) {
              recordProductSolidsResult(result, {
                questionID: productSolidsQuestionID(product.productID, 'volume'),
                productID: product.productID,
                formRLine: process.formRLine,
                releaseProfileName: profile.name,
                processName: process.processName,
                formulaOutputType,
                prompt: `By volume, what percentage of ${product.productName} is solids?`,
                subType: 'solidsPercentageByVolume',
              });
            }
            continue;
          } else if (
            (nameBare === productUsageLbVar || nameBare === productUsageGalVar) &&
            products.length > 0 &&
            emitProductUsageQuestions
          ) {
            for (const product of products) {
              recordProductUsageResult(result, {
                questionID: productUsageQuestionID(product.productID),
                productID: product.productID,
                prompt: `How many ${product.usageUnits} of ${product.productName} did you use?`,
                specificGravity: product.specificGravity,
                units: product.usageUnits,
                formRLine: process.formRLine,
                releaseProfileName: profile.name,
                processName: process.processName,
                formulaOutputType,
              });
            }
            continue;
          }

          const question = questionsByVariableName.get(nameBare);
          if (question) {
            if (question.definition.isChemicalSpecific) {
              let chemicals: CasOrCategory[];
              if (annotation) {
                chemicals = [annotation as CasOrCategory];
              } else if (chemicalRestrictions === 'noChemicals') {
                chemicals = [];
              } else {
                // 'allVolatile' and 'allSolid' are treated like 'none' for question gathering.
                // These modes are only allowed on formulas that use product concentration
                // variables, which are not questionnaire questions (they pull data from product
                // chemical composition). So in practice, this branch rarely applies — but if the
                // formula also references a separate chemical-specific question variable, we
                // include all threshold chemicals since we don't have product-level volatile/solid
                // classification here.
                chemicals = relevantChemicals.filter(
                  (casOrCategory) =>
                    chemicalRestrictions === 'none' ||
                    chemicalRestrictions === 'allVolatile' ||
                    chemicalRestrictions === 'allSolid' ||
                    chemicalRestrictions.has(casOrCategory),
                );
              }

              chemicals.push(
                ...(chemicalSpecificQuestionsForcedByQuestionID.get(question.definition.questionID) ?? []),
              );

              chemicals.sort((a, b) => {
                const aCongenerIndex = dioxinCongenerToIndex[a];
                const bCongenerIndex = dioxinCongenerToIndex[b];
                if (typeof aCongenerIndex === 'number' && typeof bCongenerIndex === 'number') {
                  return aCongenerIndex - bCongenerIndex;
                }

                return a.localeCompare(b);
              });

              for (const casOrCategory of chemicals) {
                recordNumberResult(result, {
                  baseQuestionID: question.definition.questionID,
                  basePrompt: question.description,
                  units: question.units,
                  formRLine: process.formRLine,
                  releaseProfileName: profile.name,
                  processName: process.processName,
                  casOrCategory,
                  isAutoFilledFromPriorYear: question.definition.isAutoFilledFromPriorYear,
                  facilityProvidesSourceDocumentation: question.definition.facilityProvidesSourceDocumentation,
                  formulaOutputType,
                });
              }
            } else {
              recordNumberResult(result, {
                baseQuestionID: question.definition.questionID,
                basePrompt: question.description,
                units: question.units,
                formRLine: process.formRLine,
                releaseProfileName: profile.name,
                processName: process.processName,
                casOrCategory: null,
                isAutoFilledFromPriorYear: question.definition.isAutoFilledFromPriorYear,
                facilityProvidesSourceDocumentation: question.definition.facilityProvidesSourceDocumentation,
                formulaOutputType,
              });
            }
          }

          const mix = mixCalculationsByVariableName.get(nameBare);
          if (mix) {
            if (mix.definition.percentageType !== 'questionnaire') {
              continue;
            }

            for (const component of mix.definition.components) {
              recordNumberResult(result, {
                baseQuestionID: mixComponentToQuestionID(component),
                basePrompt: mix.definition.questionPrompt + ` - What is the percentage of ${component.name}? (0-100)`,
                units: '%',
                formRLine: process.formRLine,
                releaseProfileName: profile.name,
                processName: process.processName,
                casOrCategory: null,
                isAutoFilledFromPriorYear: false,
                facilityProvidesSourceDocumentation: false,
                formulaOutputType,
              });
            }
          }

          const wasteStream = wasteStreamsByVariableName.get(nameBare);
          if (wasteStream) {
            if (wasteStream.definition.source.type === 'formula') {
              const maybeIdentifiers = VariableNameFinder.findIdentifiersInString(
                wasteStream.definition.source.formula,
              );
              if (maybeIdentifiers.status === 'error') {
                console.error(
                  `questionsRequiredFromReleaseProfiles in wasteStream:`,
                  profile,
                  wasteStream,
                  maybeIdentifiers.error,
                );
              } else {
                identifiersToProcess.push(...maybeIdentifiers.identifiers);
              }
            }

            recordWasteStreamResult(result, {
              questionID: wasteStreamToQuestionID(wasteStream),
              wasteStreamID: wasteStream.definition.wasteStreamID,
              prompt: wasteStream.description,
              units: wasteStream.units,
              formRLine: process.formRLine,
              releaseProfileName: profile.name,
              processName: process.processName,
              type:
                wasteStream.definition.source.type === 'questionnaire'
                  ? 'wasteStreamDirectInput'
                  : 'wasteStreamAllocation',
              formulaOutputType,
            });
          }
        }
      }
    }
  }

  // Ensure waste stream inputs appear last, so that allocations will have their full computed values available:
  return Array.from(result.values()).sort((a, b) => {
    const aIsWasteStream = a.type === 'wasteStreamDirectInput' || a.type === 'wasteStreamAllocation';
    const bIsWasteStream = b.type === 'wasteStreamDirectInput' || b.type === 'wasteStreamAllocation';

    if (aIsWasteStream && !bIsWasteStream) {
      return 1;
    } else if (!aIsWasteStream && bIsWasteStream) {
      return -1;
    }

    return 0;
  });
}

export function questionnaireComputeCompletionStatus(
  mode: Exclude<QuestionnaireMode, { type: 'emissions' }>,
  facility: {
    facilityID: FacilityID;
    state: string;
  },
  thresholdChemicalsPrior: CasOrCategory[],
  thresholdChemicalsCurrent: Pick<
    ThresholdBrief,
    'casOrCategory' | 'casOriginals' | 'usageTypeID' | 'isIncludedInFormRReport'
  >[],
  releaseProfileVariables: ReleaseProfilesAndVariables,
  productUsage: {
    productID: ProductID;
    totalPoundsUsed: number;
    totalGallonsUsed: number | null;
    chemicals: { casOrCategoryMapped: CasOrCategory }[];
  }[],
  processes: ReleaseProfileProcessWithChildren[],
  answers: QuestionnaireAnswers,
  // Pass this only if you're on the frontend and the user can update answers on-the-fly. Otherwise, null will be able
  // to check for completion by looking in the `processes` argument:
  productAnswers: Partial<
    Record<`${ProductID}-${'solidsPercentageByWeight' | 'solidsPercentageByVolume'}`, number>
  > | null,
): QuestionnaireStatusItem {
  const ratioEntryValid = (entry: QuestionnaireAnswers['productionOrActivityRatio']): boolean => {
    if (entry.type === 'N/A') {
      return true;
    }

    return (
      entry.type !== null &&
      typeof entry.currentYearValue === 'number' &&
      typeof entry.priorYearValue === 'number' &&
      entry.priorYearValue !== 0
    );
  };

  const thresholdStatuses = new Map<CasOrCategory, 'new' | 'old' | 'same'>();
  for (const casOrCategory of thresholdChemicalsPrior) {
    thresholdStatuses.set(casOrCategory, 'old');
  }
  for (const { casOrCategory } of thresholdChemicalsCurrent) {
    const existingStatus = thresholdStatuses.get(casOrCategory);
    if (existingStatus === 'old' || existingStatus === 'same') {
      thresholdStatuses.set(casOrCategory, 'same');
    } else {
      thresholdStatuses.set(casOrCategory, 'new');
    }
  }

  const thresholdChemicalsCurrentPlusOriginals = thresholdChemicalsPlusOriginals(thresholdChemicalsCurrent);
  const questionsRequired = questionsRequiredFromReleaseProfiles(
    mode,
    thresholdChemicalsCurrentPlusOriginals,
    answers.releaseProfileFormulaChoices,
    processes,
    releaseProfileVariables,
    releaseProfileAnnotatableVariableNames(releaseProfileVariables),
  );

  const wasteStreamTotals = facilityWasteStreamTotalsCalculate(
    mode,
    releaseProfileVariables,
    new Map(productUsage.map((p) => [p.productID, p])),
    processes,
    new Set(thresholdChemicalsCurrentPlusOriginals),
    {
      releaseProfileAnswers: answers.releaseProfileAnswers,
      releaseProfileFormulaChoices: answers.releaseProfileFormulaChoices,
      wasteTransfers: answers.wasteTransfers,
    },
  ).totals;

  const wasteTransfersArr = Object.values(answers.wasteTransfers);

  let questionsTotal = 0;
  let questionsAnswered = 0;
  const questionAnswersMissing: QuestionnaireStatusItem['questionAnswersMissing'] = [];

  for (const question of questionsRequired) {
    if (question.type === 'number') {
      questionsTotal += 1;
      if (typeof answers.releaseProfileAnswers[question.questionID] === 'number') {
        questionsAnswered += 1;
      } else if (answers.releaseProfileChemicalNaAnswers[question.questionID]) {
        questionsAnswered += 1;
      } else {
        questionAnswersMissing.push(question.questionID);
      }
    } else if (question.type === 'releaseProfileFormulaChoice') {
      questionsTotal += 1;
      const answer = answers.releaseProfileFormulaChoices[question.choiceListID];
      if (typeof answer === 'string' && answer.startsWith('formula_choice_')) {
        questionsAnswered += 1;
      } else {
        questionAnswersMissing.push(question.questionID);
      }
    } else if (question.type === 'wasteStreamDirectInput') {
      questionsTotal += 1;

      if (wasteTransferExistsAndHasAllValues(question.wasteStreamID, wasteTransfersArr, 'wasteStreamDirectInput')) {
        questionsAnswered += 1;
      } else {
        questionAnswersMissing.push(question.questionID);
      }
    } else if (question.type === 'wasteStreamAllocation') {
      const totalToAllocate = wasteStreamTotals[question.wasteStreamID]?.total ?? null;
      if (totalToAllocate === null || totalToAllocate === 0) {
        continue;
      }
      questionsTotal += 1;

      const warning = wasteStreamAllocationWarning(question, totalToAllocate, wasteTransfersArr);
      if (
        warning.status === 'no-warnings' &&
        wasteTransferExistsAndHasAllValues(question.wasteStreamID, wasteTransfersArr, 'wasteStreamAllocation')
      ) {
        questionsAnswered += 1;
      } else {
        questionAnswersMissing.push(question.questionID);
      }
    } else if (question.type === 'wasteStreamAttestedZero') {
      questionsTotal += 1;

      if (wasteTransferExistsAndHasAllValues(question.wasteStreamID, wasteTransfersArr, 'wasteStreamAttestedZero')) {
        questionsAnswered += 1;
      } else {
        questionAnswersMissing.push(question.questionID);
      }
    } else if (question.type === 'productSolids') {
      questionsTotal += 1;
      let found = false;

      if (productAnswers && productAnswers[`${question.productID}-${question.subType}`] !== undefined) {
        found = true;
      } else if (!productAnswers) {
        processLoop: for (const process of processes) {
          for (const profile of process.profiles) {
            for (const product of profile.products) {
              if (product.productID === question.productID && product[question.subType] !== null) {
                found = true;
                break processLoop;
              }
            }
          }
        }
      }

      if (found) {
        questionsAnswered += 1;
      } else {
        questionAnswersMissing.push(question.questionID);
      }
    } else {
      const unexpected: QuestionBriefProductUsage = question;
      console.error(`questionnaireStatusForTenant: Unexpected question type: ${JSON.stringify(unexpected)}`);
    }
  }

  if (mode.type === 'triRelease') {
    if (stateSpecificGuidanceByState.get(facility.state)) {
      questionsTotal += 1;

      if (answers.stateSpecificPlanning.trim() !== '') {
        questionsAnswered++;
      } else {
        questionAnswersMissing.push('stateSpecificPlanning');
      }
    }

    questionsTotal += 1;
    if (answers.anySignificantChanges.trim() !== '') {
      questionsAnswered++;
    } else {
      questionAnswersMissing.push('anySignificantChanges');
    }

    const requiredChemicals = new Set(thresholdChemicalsCurrent.map((c) => c.casOrCategory));
    // Sections 3 (chemicalActivitiesAndUses) and 4 (maxOnSiteAmount) only display chemicals that are included in the report:
    const formRRequiredChemicals = new Set(
      thresholdChemicalsCurrent.filter((c) => c.isIncludedInFormRReport).map((c) => c.casOrCategory),
    );

    questionsTotal += 3;
    const ratioByChemical = answers.productionOrActivityRatio.byChemical ?? {};
    if (answers.productionOrActivityRatio.isChemicalSpecific) {
      if (requiredChemicals.size === 0) {
        if (ratioEntryValid(answers.productionOrActivityRatio)) {
          questionsAnswered += 3;
        } else {
          questionAnswersMissing.push('productionOrActivityRatio');
        }
      } else {
        let allComplete = true;
        for (const casOrCategory of requiredChemicals) {
          const entry = ratioByChemical[casOrCategory];
          if (!entry || !ratioEntryValid(entry)) {
            allComplete = false;
            break;
          }
        }
        if (allComplete) {
          questionsAnswered += 3;
        } else {
          questionAnswersMissing.push('productionOrActivityRatio');
        }
      }
    } else if (ratioEntryValid(answers.productionOrActivityRatio)) {
      questionsAnswered += 3;
    } else {
      questionAnswersMissing.push('productionOrActivityRatio');
    }

    questionsTotal += 1;
    if (
      thresholdChemicalsCurrent.every(({ casOrCategory, usageTypeID, isIncludedInFormRReport }) => {
        const uses = answers.chemicalActivitiesAndUses[casOrCategory];
        return (
          !isIncludedInFormRReport ||
          (uses && formRChemicalActivitiesAndUsesIsComplete(casOrCategory, new Set([usageTypeID]), uses))
        );
      })
    ) {
      questionsAnswered += 1;
    } else {
      questionAnswersMissing.push('chemicalActivitiesAndUses');
    }

    if (answers.maxOnSiteAmount.method === 'inventory-backlog-facility-wide') {
      questionsTotal += 1;
      if (formRRequiredChemicals.size === 0 || answers.maxOnSiteAmount.inventoryBacklogInWeeksOnSite !== '') {
        questionsAnswered += 1;
      } else {
        questionAnswersMissing.push('maxOnSiteAmount');
      }
    } else if (answers.maxOnSiteAmount.method === 'inventory-backlog-per-product') {
      let missed = false;
      const hasAnswers = new Set(
        answers.maxOnSiteAmount.perProductBacklogWeeks
          .filter((p) => typeof p.backlogWeeks === 'number')
          .map((p) => p.productID),
      );
      for (const { productID, totalPoundsUsed, chemicals } of productUsage) {
        if (totalPoundsUsed <= 0 || !chemicals.some((c) => formRRequiredChemicals.has(c.casOrCategoryMapped))) {
          continue;
        }

        questionsTotal += 1;
        if (hasAnswers.has(productID)) {
          questionsAnswered += 1;
        } else {
          missed = true;
        }
      }
      if (missed) {
        questionAnswersMissing.push('maxOnSiteAmount');
      }
    } else if (answers.maxOnSiteAmount.method === 'per-product-max') {
      let missed = false;
      const hasAnswers = new Set(
        answers.maxOnSiteAmount.perProductMaxQuantities
          .filter((p) => typeof p.maxQuantityOnSite === 'number')
          .map((p) => p.productID),
      );
      for (const { productID, totalPoundsUsed, chemicals } of productUsage) {
        if (totalPoundsUsed <= 0 || !chemicals.some((c) => formRRequiredChemicals.has(c.casOrCategoryMapped))) {
          continue;
        }

        questionsTotal += 1;
        if (hasAnswers.has(productID)) {
          questionsAnswered += 1;
        } else {
          missed = true;
        }
      }
      if (missed) {
        questionAnswersMissing.push('maxOnSiteAmount');
      }
    } else if (answers.maxOnSiteAmount.method === 'direct-entry') {
      let missed = false;
      for (const casOrCategory of formRRequiredChemicals) {
        questionsTotal += 1;
        if (typeof answers.maxOnSiteAmount.chemicalPeakPounds[casOrCategory] === 'number') {
          questionsAnswered += 1;
        } else {
          missed = true;
        }
      }
      if (missed) {
        questionAnswersMissing.push('maxOnSiteAmount');
      }
    } else if (answers.maxOnSiteAmount.method === 'direct-override') {
      let missed = false;
      for (const casOrCategory of formRRequiredChemicals) {
        questionsTotal += 1;
        if (typeof answers.maxOnSiteAmount.chemicalOverrideRangeCodes[casOrCategory] === 'string') {
          questionsAnswered += 1;
        } else {
          missed = true;
        }
      }
      if (missed) {
        questionAnswersMissing.push('maxOnSiteAmount');
      }
    }

    questionsTotal += 1;
    if (
      answers.wasteTreatment.isApplicable === false ||
      (answers.wasteTreatment.isApplicable === true && Object.values(answers.wasteTreatment.treatments).length > 0)
    ) {
      questionsAnswered += 1;
    } else {
      questionAnswersMissing.push('wasteTreatment');
    }

    questionsTotal += 1;
    if (
      thresholdChemicalsCurrent.every(({ casOrCategory }) => {
        return (
          answers.onSiteEnergyRecovery[casOrCategory]?.isApplicable === false ||
          (answers.onSiteEnergyRecovery[casOrCategory]?.isApplicable === true &&
            answers.onSiteEnergyRecovery[casOrCategory].methods.length > 0)
        );
      })
    ) {
      questionsAnswered += 1;
    } else {
      questionAnswersMissing.push('onSiteEnergyRecovery');
    }

    questionsTotal += 1;
    if (
      thresholdChemicalsCurrent.every(({ casOrCategory }) => {
        return (
          answers.onSiteRecycling[casOrCategory]?.isApplicable === false ||
          (answers.onSiteRecycling[casOrCategory]?.isApplicable === true &&
            answers.onSiteRecycling[casOrCategory].methods.length > 0)
        );
      })
    ) {
      questionsAnswered += 1;
    } else {
      questionAnswersMissing.push('onSiteRecycling');
    }

    for (const { casOrCategory } of thresholdChemicalsCurrent) {
      if (answers.potwDistributionsKnown[casOrCategory]) {
        questionsTotal += 1;
        let total = 0;
        for (const value of Object.values(answers.potwDistributionsKnown[casOrCategory])) {
          total += value;
        }
        if (total === 100) {
          questionsAnswered += 1;
        } else {
          questionAnswersMissing.push('potwDistributionsKnown');
        }
      }
    }

    questionsTotal += 1;
    if (
      answers.sourceReductionActivities.isApplicable === false ||
      (answers.sourceReductionActivities.isApplicable === true &&
        answers.sourceReductionActivities.activities.length > 0)
    ) {
      questionsAnswered += 1;
    } else {
      questionAnswersMissing.push('sourceReductionActivities');
    }
  }

  return {
    facilityID: facility.facilityID,
    questionsAnswered,
    questionsTotal,
    questionAnswersMissing,
    thresholds: Array.from(thresholdStatuses.entries()).map(([casOrCategory, status]) => ({
      casOrCategory,
      status,
    })),
  };
}

const casPlaceholderRegexp = /\[CAS:([^\]]+)\]/;
export function questionsFindCasPlaceholders(questions: QuestionBrief[]): Set<CasOrCategory> {
  const result = new Set<CasOrCategory>();

  for (const question of questions) {
    const match = casPlaceholderRegexp.exec(question.prompt);
    if (match) {
      const casOrCategory = match[1] as CasOrCategory;
      result.add(casOrCategory);
    }
  }

  return result;
}

export function questionsReplaceCasPlaceholders(
  questions: QuestionBrief[],
  lookup: (casOrCategory: CasOrCategory) => string | null,
): void {
  for (const question of questions) {
    const match = casPlaceholderRegexp.exec(question.prompt);
    if (match) {
      const casOrCategory = match[1] as CasOrCategory;
      const name = lookup(casOrCategory);

      if (name && name !== casOrCategory) {
        question.prompt = question.prompt.replace(casPlaceholderRegexp, `${name} (${casOrCategory})`);
      } else {
        question.prompt = question.prompt.replace(casPlaceholderRegexp, casOrCategory);
      }
    }
  }
}

export function questionnaireFindChemicalSpecificQuestions(
  questions: QuestionBrief[],
): Map<ReleaseProfileQuestionID, Set<CasOrCategory>> {
  const result = new Map<ReleaseProfileQuestionID, Set<CasOrCategory>>();

  for (const question of questions) {
    if (question.type === 'number' && question.casOrCategory) {
      if (!result.has(question.baseQuestionID)) {
        result.set(question.baseQuestionID, new Set());
      }

      result.get(question.baseQuestionID)!.add(question.casOrCategory);
    }
  }

  return result;
}
